import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  MessageSquare,
  Plus,
  Send,
  Search,
  X,
  AlertCircle,
  CheckCircle2,
  Clock,
  Circle,
  LifeBuoy,
} from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Modal } from "@/components/Modal";
import { Badge, type BadgeColor } from "@/components/DataTable";
import {
  listTickets,
  getTicket,
  createTicket,
  sendTicketMessage,
  getTicketCategories,
  ApiError,
  type Ticket,
  type TicketPriority,
  type TicketStatus,
} from "@/lib/api";
import { formatDate, formatRelative } from "@/lib/format";

// ---------------------------------------------------------------------------

function statusColor(s: TicketStatus): BadgeColor {
  if (s === "open") return "info";
  if (s === "in_progress") return "warning";
  if (s === "resolved") return "success";
  return "light";
}

const STATUS_LABEL: Record<TicketStatus, string> = {
  open: "Ouvert",
  in_progress: "En cours",
  resolved: "Résolu",
  closed: "Fermé",
};

function priorityColor(p: TicketPriority): BadgeColor {
  if (p === "urgent") return "danger";
  if (p === "high") return "warning";
  if (p === "medium") return "info";
  return "light";
}

const PRIORITY_LABEL: Record<TicketPriority, string> = {
  low: "Faible",
  medium: "Moyenne",
  high: "Élevée",
  urgent: "Urgente",
};

// ---------------------------------------------------------------------------

export function SupportPage() {
  const qc = useQueryClient();
  const auth = useAuth();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | TicketStatus>("all");
  const [openTicketId, setOpenTicketId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const listQuery = useQuery<Ticket[]>({
    queryKey: ["tickets"],
    queryFn: listTickets,
  });

  const tickets = listQuery.data ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tickets.filter((t) => {
      if (statusFilter !== "all" && t.status !== statusFilter) return false;
      if (!q) return true;
      return (
        t.subject.toLowerCase().includes(q) ||
        t.ticket_id.toLowerCase().includes(q) ||
        t.category.toLowerCase().includes(q)
      );
    });
  }, [tickets, search, statusFilter]);

  const stats = useMemo(() => {
    const acc = { total: 0, open: 0, in_progress: 0, resolved: 0 };
    for (const t of tickets) {
      acc.total += 1;
      if (t.status === "open") acc.open += 1;
      else if (t.status === "in_progress") acc.in_progress += 1;
      else if (t.status === "resolved") acc.resolved += 1;
    }
    return acc;
  }, [tickets]);

  return (
    <div>
      <PageHeader
        title="Support"
        breadcrumbs={[{ label: "Mon espace" }, { label: "Support" }]}
        actions={
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="btn btn-primary btn-sm"
          >
            <Plus size={14} />
            Nouveau ticket
          </button>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <StatMini
          label="Total tickets"
          value={stats.total}
          icon={<MessageSquare size={14} />}
          color="primary"
        />
        <StatMini
          label="Ouverts"
          value={stats.open}
          icon={<Circle size={14} />}
          color="info"
        />
        <StatMini
          label="En cours"
          value={stats.in_progress}
          icon={<Clock size={14} />}
          color="warning"
        />
        <StatMini
          label="Résolus"
          value={stats.resolved}
          icon={<CheckCircle2 size={14} />}
          color="success"
        />
      </div>

      {/* Toolbar */}
      <div className="card mb-4">
        <div className="card-body flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="relative flex-1">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un ticket (sujet, catégorie, ID)…"
              className="form-input pl-9 py-2 text-sm w-full"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            className="form-input py-2 px-3 text-sm w-auto"
          >
            <option value="all">Tous statuts</option>
            <option value="open">Ouverts</option>
            <option value="in_progress">En cours</option>
            <option value="resolved">Résolus</option>
            <option value="closed">Fermés</option>
          </select>
        </div>
      </div>

      {/* List */}
      <div className="card">
        {listQuery.isLoading ? (
          <div className="p-6 text-center text-sm text-slate-500">Chargement…</div>
        ) : listQuery.isError ? (
          <div className="p-6 text-center text-sm text-danger-700 flex items-center justify-center gap-2">
            <AlertCircle size={14} />
            Impossible de charger vos tickets.
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <LifeBuoy size={32} className="mx-auto text-slate-300 mb-2" />
            <div className="text-sm text-slate-500">
              {tickets.length === 0
                ? "Vous n'avez pas encore de ticket. Cliquez sur \"Nouveau ticket\" pour contacter le support."
                : "Aucun ticket ne correspond à vos filtres."}
            </div>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map((t) => (
              <button
                key={t.ticket_id}
                type="button"
                onClick={() => setOpenTicketId(t.ticket_id)}
                className="w-full text-left px-4 py-4 hover:bg-slate-50 transition-colors flex items-start gap-3"
              >
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                    t.unread_by_merchant
                      ? "bg-brand-500 text-white"
                      : "bg-slate-100 text-slate-500"
                  }`}
                >
                  <MessageSquare size={16} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-[#181c32]">{t.subject}</div>
                    {t.unread_by_merchant && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-brand-50 text-brand-700">
                        Nouveau
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="font-mono">{t.ticket_id}</span>
                    <span>·</span>
                    <span>{t.category}</span>
                    <span>·</span>
                    <span>
                      {t.message_count ?? 0} message{(t.message_count ?? 0) > 1 ? "s" : ""}
                    </span>
                    <span>·</span>
                    <span>{formatRelative(t.updated_at || t.created_at)}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <Badge color={statusColor(t.status)}>{STATUS_LABEL[t.status]}</Badge>
                  <Badge color={priorityColor(t.priority)}>{PRIORITY_LABEL[t.priority]}</Badge>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {creating && (
        <CreateTicketModal
          onClose={() => setCreating(false)}
          onCreated={(t) => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ["tickets"] });
            setOpenTicketId(t.ticket_id);
          }}
          defaultEmail={auth.status === "authenticated" ? auth.user.email : ""}
        />
      )}

      {openTicketId && (
        <TicketDetailModal
          ticketId={openTicketId}
          onClose={() => {
            setOpenTicketId(null);
            qc.invalidateQueries({ queryKey: ["tickets"] });
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function StatMini({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  color: "primary" | "info" | "warning" | "success";
}) {
  const cls: Record<typeof color, string> = {
    primary: "text-brand-600 bg-brand-50",
    info: "text-info-600 bg-info-50",
    warning: "text-warning-700 bg-warning-50",
    success: "text-success-600 bg-success-50",
  };
  return (
    <div className="card card-body py-3">
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${cls[color]}`}>
          {icon}
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-[#7e8299] font-bold">
            {label}
          </div>
          <div className="text-lg font-bold text-[#181c32] leading-tight">
            {value.toLocaleString("fr-FR")}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function CreateTicketModal({
  onClose,
  onCreated,
  defaultEmail,
}: {
  onClose: () => void;
  onCreated: (t: Ticket) => void;
  defaultEmail: string;
}) {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState(defaultEmail);
  const [priority, setPriority] = useState<TicketPriority>("medium");
  const [category, setCategory] = useState<string>("Technical");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const catsQuery = useQuery<string[]>({
    queryKey: ["ticket-categories"],
    queryFn: getTicketCategories,
    staleTime: 5 * 60_000,
  });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!subject.trim() || !description.trim()) {
      setError("Sujet et description sont requis.");
      return;
    }
    setSubmitting(true);
    try {
      const t = await createTicket({
        subject: subject.trim(),
        description: description.trim(),
        email: email.trim(),
        priority,
        category,
      });
      onCreated(t);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail || err.title : "Erreur lors de la création");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Nouveau ticket" size="lg">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-[#4a4a6a] mb-2">Sujet *</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Ex : Le retrait de 50 000 CDF est bloqué"
            className="form-input py-2 text-sm w-full"
            maxLength={200}
            autoFocus
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-semibold text-[#4a4a6a] mb-2">Catégorie</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="form-input py-2 text-sm w-full"
            >
              {(catsQuery.data ?? ["Technical", "Billing", "Account", "Payment", "Withdrawal", "Other"]).map(
                (c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ),
              )}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#4a4a6a] mb-2">Priorité</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value as TicketPriority)}
              className="form-input py-2 text-sm w-full"
            >
              <option value="low">{PRIORITY_LABEL.low}</option>
              <option value="medium">{PRIORITY_LABEL.medium}</option>
              <option value="high">{PRIORITY_LABEL.high}</option>
              <option value="urgent">{PRIORITY_LABEL.urgent}</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-[#4a4a6a] mb-2">
            Email de contact
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="form-input py-2 text-sm w-full"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-[#4a4a6a] mb-2">Description *</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={6}
            placeholder="Décrivez le problème en détail : ce que vous vouliez faire, ce qui s'est passé, référence(s) de transaction si applicable…"
            className="form-input py-2 text-sm w-full"
          />
        </div>

        {error && (
          <div className="rounded-lg border border-danger-100 bg-danger-50 p-3 text-xs text-danger-700 flex items-start gap-2">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <div>{error}</div>
          </div>
        )}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={submitting}
            className="btn btn-primary flex-1"
          >
            <Send size={14} />
            {submitting ? "Envoi…" : "Créer le ticket"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-light"
          >
            Annuler
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

function TicketDetailModal({
  ticketId,
  onClose,
}: {
  ticketId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const detailQuery = useQuery<Ticket>({
    queryKey: ["ticket", ticketId],
    queryFn: () => getTicket(ticketId),
  });
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const ticket = detailQuery.data;

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [ticket?.messages?.length]);

  const send = async () => {
    if (!reply.trim() || !ticket) return;
    setSending(true);
    setError(null);
    try {
      await sendTicketMessage(ticket.ticket_id, { message: reply.trim() });
      setReply("");
      detailQuery.refetch();
      qc.invalidateQueries({ queryKey: ["tickets"] });
    } catch (err) {
      setError(err instanceof ApiError ? err.detail || err.title : "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={
        ticket ? (
          <div className="flex items-center gap-2 flex-wrap">
            <span>{ticket.subject}</span>
            <Badge color={statusColor(ticket.status)}>{STATUS_LABEL[ticket.status]}</Badge>
            <Badge color={priorityColor(ticket.priority)}>
              {PRIORITY_LABEL[ticket.priority]}
            </Badge>
          </div>
        ) : (
          "Ticket"
        )
      }
      subtitle={
        ticket ? (
          <span className="flex items-center gap-2 text-xs flex-wrap">
            <span className="font-mono">{ticket.ticket_id}</span>
            <span>·</span>
            <span>{ticket.category}</span>
            <span>·</span>
            <span>Créé le {formatDate(ticket.created_at)}</span>
          </span>
        ) : null
      }
      size="xl"
    >
      {detailQuery.isLoading ? (
        <div className="p-6 text-center text-sm text-slate-500">Chargement…</div>
      ) : !ticket ? (
        <div className="p-6 text-center text-sm text-danger-700">Ticket introuvable.</div>
      ) : (
        <div className="flex flex-col" style={{ maxHeight: "70vh" }}>
          {/* Description initiale */}
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 mb-4 text-sm text-[#3f4254] whitespace-pre-wrap">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">
              Description initiale
            </div>
            {ticket.description}
          </div>

          {/* Message thread */}
          <div className="flex-1 overflow-y-auto space-y-3 pr-1 mb-4 min-h-[120px]">
            {(ticket.messages ?? []).length === 0 ? (
              <div className="text-center text-xs text-slate-400 py-4">
                Aucun message dans ce ticket pour l'instant.
              </div>
            ) : (
              (ticket.messages ?? []).map((m) => {
                const isUser = m.sender_type === "user";
                return (
                  <div
                    key={m.id}
                    className={`flex ${isUser ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[75%] rounded-2xl px-3.5 py-2.5 text-sm ${
                        isUser
                          ? "bg-brand-500 text-white"
                          : "bg-slate-100 text-[#181c32]"
                      }`}
                    >
                      <div
                        className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${
                          isUser ? "text-brand-100" : "text-slate-500"
                        }`}
                      >
                        {isUser ? "Vous" : `Support · ${m.sender_name || "Moko"}`} ·{" "}
                        {formatRelative(m.created_at)}
                      </div>
                      <div className="whitespace-pre-wrap">{m.message}</div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={bottomRef} />
          </div>

          {/* Reply */}
          {ticket.status !== "closed" && ticket.status !== "resolved" && (
            <div className="border-t border-slate-100 pt-3">
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Répondre au support…"
                rows={3}
                className="form-input py-2 text-sm w-full"
              />
              {error && (
                <div className="mt-2 text-xs text-danger-700 flex items-start gap-1.5">
                  <X size={12} className="mt-0.5 shrink-0" />
                  {error}
                </div>
              )}
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  onClick={send}
                  disabled={sending || !reply.trim()}
                  className="btn btn-primary btn-sm"
                >
                  <Send size={14} />
                  {sending ? "Envoi…" : "Envoyer"}
                </button>
              </div>
            </div>
          )}

          {(ticket.status === "closed" || ticket.status === "resolved") && (
            <div className="border-t border-slate-100 pt-3 text-xs text-slate-500 text-center">
              Ce ticket est {STATUS_LABEL[ticket.status].toLowerCase()}. Créez un nouveau ticket
              si le problème persiste.
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
